import {
  Component,
  ViewEncapsulation,
  NgZone,
  ElementRef,
  ApplicationRef,
  Injector,
  OnDestroy,
} from "@angular/core";
import { BfxBlotterOrders } from "../bfx-blotter-orders/bfx-blotter-orders";
import { RowNode } from "ag-grid-community/main";

import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { GoldenLayoutPopout } from "../golden-layout/golden-layout-popout";
import {
  GLComponentAlertStatus,
  GoldenLayoutServiceProfileConfig,
} from "../golden-layout/golden-layout-component";
import { TranslateService } from "@ngx-translate/core";
import { BfxBlotterFilterService } from "../bfx-blotter-filter/bfx-blotter-filter.service";
import { BfxOrderBlotterFilteringService } from "../bfx-blotter-orders/bfx-blotter-orders-filter.service";
import {
  InboxTableDefinitionFactory,
  SystemConfigPromiseService,
  ProfilePromiseActionService,
  UserTemperatureService,
} from "shared/cubiq-services";
import { BfxBaseBlotterColumnSelectorService } from "../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxBlotterInboxFilter } from "./bfx-blotter-inbox-filter";
import { BfxBlotterInboxService } from "./bfx-blotter-inbox.service";
import { BfxBlotterInboxContextMenuService } from "./bfx-blotter-inbox-contextmenu.service";
import { BfxUserSettingsService } from "../bfx-user-settings/bfx-user-settings.service";
import { provideBlotter } from "../bfx-base-blotter/bfx-base-blotter";
import { BfxMouseModifierKeyService } from "../bfx-common/services/bfx-mouse-modifier-key.service";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BfxSaveProfileNotificationService } from "../bfx-common/services/bfx-save-profile-notification.service";
import { OrderTypeRenderer } from "../bfx-blotter-orders/renderers/OrderTypeRenderer";
import { SpotTrackingRenderer } from "../bfx-blotter-orders/renderers/SpotTrackingRenderer";
import { PercentAwayRenderer } from "../bfx-blotter-orders/renderers/PercentAwayRenderer";
import { FwdPtsRenderer } from "../bfx-blotter-orders/renderers/FwdPtsRenderer";
import { MarketMidRenderer } from "../bfx-blotter-orders/renderers/MarketMidRenderer";
import { FillRenderer } from "../bfx-blotter-orders/renderers/FillRenderer";
import { OrdersTableMode } from "shared/cubiq-services/model/OrdersTableMode";
import { Subscription } from "rxjs";
import { FillPercentRenderer } from "app/bfx-blotter-orders/renderers/FillPercentRenderer";
import { BfxVwapOrdersService } from "app/bfx-vwap/bfx-vwap-orders.service";
import { BfxVwapInfo } from "app/bfx-vwap/bfx-vwap-info";
import { FilterConfiguration } from "../bfx-blotter-filter/FilterConfiguration";
import { CommonIntentRenderer } from "app/bfx-blotter-orders/renderers/CommonIntentRenderer";

@Component({
  selector: "bfx-blotter-inbox",
  templateUrl: "./html/bfx-blotter-inbox.html",
  encapsulation: ViewEncapsulation.None,
  providers: [provideBlotter(BfxBlotterInbox)],
  standalone: false,
})
export class BfxBlotterInbox extends BfxBlotterOrders implements OnDestroy {
  private lastItemCounter: number = -1;
  private viewActive: boolean = false;
  private newAlert: boolean = true;
  private modeSubscription: Subscription = null;

  public frameworkComponents: any = {
    orderTypeRenderer: OrderTypeRenderer,
    intentRenderer: CommonIntentRenderer,
    spotTrackingRenderer: SpotTrackingRenderer,
    percentAwayRenderer: PercentAwayRenderer,
    marketMidRenderer: MarketMidRenderer,
    fillRenderer: FillRenderer,
    fillPercRenderer: FillPercentRenderer,
    fwdPtsRenderer: FwdPtsRenderer,
  };

  constructor(
    BfxBlotterElement: ElementRef,
    applicationRef: ApplicationRef,
    injector: Injector,
    public service: BfxBlotterInboxService,
    _contextMenuService: BfxBlotterInboxContextMenuService,
    zone: NgZone,
    _tableDefinitionFactory: InboxTableDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    filteringService: BfxOrderBlotterFilteringService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    protected inboxFilter: BfxBlotterInboxFilter,
    profileActionService: ProfilePromiseActionService,
    sysConfigService: SystemConfigPromiseService,
    userSettingsService: BfxUserSettingsService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    protected userProfileService: BfxUserProfileService,
    protected userTempService: UserTemperatureService,
    protected vwapOrdersService: BfxVwapOrdersService,
    protected vwapInfo: BfxVwapInfo,
  ) {
    super(
      BfxBlotterElement,
      applicationRef,
      injector,
      service,
      _contextMenuService,
      zone,
      _tableDefinitionFactory,
      layoutService,
      translationService,
      blotterFilterService,
      filteringService,
      columnSelectorService,
      profileActionService,
      sysConfigService,
      userSettingsService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
      userTempService,
      vwapOrdersService,
      vwapInfo,
    );
    this.blotterId = "blotter-inbox";

    if (this.service.hasMode) {
      // use mode of last closed inbox if available
      // this *must* be before mode$ subscription to avoid looping
      this.mode = this.service.mode;
      // prevent BfxBlotterOrders.initializeColumnDefinitions()
      this.isRestoredFromConfig = true;
    }

    this.modeSubscription = this.mode$.subscribe((mode: OrdersTableMode) => {
      this.service.mode = mode;
    });
  }

  getVwapSource(): string {
    return "Inbox";
  }
  public glInit(container: any) {}

  public glComponentIsSingleton() {
    return true;
  }

  public glServiceProfileConfig(): GoldenLayoutServiceProfileConfig {
    return this.service.glServiceProfileConfig();
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();

    if (this.modeSubscription != null) this.modeSubscription.unsubscribe();
  }

  protected initializeInMemory(): void {
    super.initializeInMemory();
    this.inboxFilter.isReady().then(() => {
      this.service.isReady().then(() => {
        this.blotterFilterService.applyFilterConfig({
          blotterId: this.blotterId,
          items: this.service.getFilterConfiguration(),
        });

        // super.filterSelectionChanged();

        this.service.subjectNewItems.subscribe(() => {
          this.newAlert = true;
        });

        this.service.subjectUpdatedItems.subscribe(this.onInboxItemsUpdated.bind(this));
      });
    });
  }

  protected onFullUpdateEvent(rows: Array<any>): void {
    super.onFullUpdateEvent(rows);
  }

  protected gridDataUpdated(): void {
    super.gridDataUpdated();
    this.checkBadgeNumber();
  }

  protected checkForNoRows(): void {
    super.checkForNoRows();
    this.checkBadgeNumber();
  }

  private checkBadgeNumber() {
    if (this.gridOptions.api) {
      if (this.lastItemCounter !== this.getBadgeNumber()) {
        this.lastItemCounter = this.getBadgeNumber();
        let alertStatus = this.getAlertStatus();
        this.layoutService.updateBadge(this, this.lastItemCounter, alertStatus);
      }
    }
  }

  protected onDeleteEvent(rowId: string): void {
    super.onDeleteEvent(rowId);
  }

  protected getBadgeNumber(): number {
    return this.service.getItemCount();
  }

  protected afterFilteringCompleted(): void {
    super.afterFilteringCompleted();
    this.lastItemCounter = this.getBadgeNumber();
    let alertStatus = this.getAlertStatus();
    this.layoutService.updateBadge(this, this.lastItemCounter, alertStatus);
  }

  setParentPopup(parentPopup: GoldenLayoutPopout) {
    parentPopup.setShowDock(true);
  }

  /**
   * External filter is always present as we have inbox filtering
   * added as external filter.
   */
  public isExternalFilterPresent(): boolean {
    return true;
  }

  public doesExternalFilterPass(node: RowNode): boolean {
    if (
      this.service.marketBar &&
      node.data[this.tableDefinition.primaryKey] ==
        this.service.marketBar[this.tableDefinition.primaryKey]
    ) {
      return this.marketBarOn && !this.removeMarkeBlotterEmpty;
    }

    let passes: boolean = this.inboxFilter.doesFilterPass(node.data, this.dealtMode);

    if (!passes && node.isSelected()) {
      node.setSelected(false);
    }

    return passes;
  }

  public componentActivated(): void {
    this.viewActive = true;
    this.newAlert = false;
  }

  public componentDeactivated(): void {
    if (this.viewActive) {
      // If it was active, then ensure that new alerts are disabled for what we currently show
      this.viewActive = false;
      this.newAlert = false;
    }
  }

  public getAlertStatus(): GLComponentAlertStatus {
    if (this.getBadgeNumber() == 0 || this.viewActive) {
      return GLComponentAlertStatus.NONE;
    }
    if (!this.viewActive && this.newAlert) {
      return GLComponentAlertStatus.ALERT_FLASH;
    }
    return GLComponentAlertStatus.ALERT;
  }

  public filterSelectionChanged(filterConfiguration: FilterConfiguration) {
    this.service.updateFilterSelection(filterConfiguration ?? []);
    this.service.updateBadgeCount(this);
    super.filterSelectionChanged(filterConfiguration);
  }

  public getProfileSettingKey(): string {
    return `${super.getProfileSettingKey()}/INBOX`;
  }

  public getFilterConfiguration(): any {
    return this.service.getFilterConfiguration();
  }

  public onCellDoubleClicked(event) {
    super.onCellDoubleClicked(event);
  }

  public shouldShowTemperatureFilter(): boolean {
    return false;
  }

  /**
   * If an item receives model update event it might actually leave
   * the inbox as it doesn't pass the embedded filter but still keep the
   * selection. This does not include partially updated items.
   *
   * @param orderIds the order ids of the records in the inbox that have
   * been updated.
   */
  protected onInboxItemsUpdated(): void {
    // orderIds.forEach((orderId: string) => {
    //     let row: RowNode = this.gridOptions.api.getRowNode(orderId);
    //     if (row && row.isSelected()) {
    //         row.setSelected(false);
    //     }
    // });
  }

  public restoreFromConfiguration(config: any): void {
    // Filters from service configuration
    if (config != null) {
      delete config.filterConfiguration;
    }

    super.restoreFromConfiguration(config);
  }

  public getComponentConfiguration(): any {
    let configuration = super.getComponentConfiguration();

    // Filters from service configuration
    if (configuration != null) {
      delete configuration.filterConfiguration;
    }

    return configuration;
  }

  protected initializeColumnDefinitions(): void {
    super.initializeColumnDefinitions();
  }
}
